import { useEffect, useCallback } from 'react';
import loadScript from '../utils/loadScript';

/**
 * Injects the reCaptcha script into the document body if both params are provided.
 * @param recaptchaScriptUrl The base URL for the reCaptcha script.
 * @param recaptchaSiteKey The site key to append to the script URL.
 * @param onLoad Optional callback when script is loaded.
 * @param onError Optional callback when script fails to load.
 */
export const useRecaptcha = (
  recaptchaScriptUrl?: string,
  recaptchaSiteKey?: string,
  onLoad?: () => void,
  onError?: () => void,
) => {
  useEffect(() => {
    if (!recaptchaScriptUrl || !recaptchaSiteKey) return;
    const scriptId = 'recaptcha-script';
    const url = `${recaptchaScriptUrl}${recaptchaSiteKey}`;
    loadScript({ url, id: scriptId })
      .then(() => {
        onLoad?.();
      })
      .catch(() => {
        onError?.();
      });
    // No cleanup: Google recommends script stays for re-use
  }, [recaptchaScriptUrl, recaptchaSiteKey, onLoad, onError]);
};

interface UseRecaptchaHandlerProps {
  recaptchaSiteKey: string;
  onSuccess: (token: string) => void;
  onError: () => void;
}

/**
 * Hook that returns a form submit handler with reCAPTCHA integration.
 * @param recaptchaSiteKey The reCAPTCHA site key.
 * @param onSuccess Callback when reCAPTCHA succeeds with token.
 * @param onError Callback when reCAPTCHA fails.
 * @returns Form submit handler function.
 */
export const useRecaptchaHandler = ({ recaptchaSiteKey, onSuccess, onError }: UseRecaptchaHandlerProps) => {
  return useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      window.grecaptcha.ready(function () {
        window.grecaptcha.execute(recaptchaSiteKey, { action: 'submit' }).then(function (token: string) {
          if (token) {
            onSuccess(token);
          } else {
            onError();
          }
        });
      });
    },
    [recaptchaSiteKey, onSuccess, onError],
  );
};

// Keep backward compatibility as default export
export default useRecaptcha;
